import type { SummaryResponse, CategoryOption } from '../hooks/useExpenses';

/* ── Category colors (mirrors backend) ────────────────────────────── */

const CATEGORY_COLORS: Record<string, string> = {
  subscription:   '#a78bfa',
  utilities:      '#facc15',
  food:           '#4ade80',
  transport:      '#22d3ee',
  health:         '#f87171',
  tech:           '#38bdf8',
  education:      '#e879f9',
  housing:        '#fb923c',
  entertainment:  '#c084fc',
  payroll:        '#34d399',
  marketing:      '#f472b6',
  office:         '#94a3b8',
  other:          '#6b7280',
};

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
] as const;

function formatMonth(monthStr: string): string {
  const [y, m] = monthStr.split('-');
  return `${MONTH_NAMES[parseInt(m, 10) - 1]} ${y}`;
}

interface Props {
  summary: SummaryResponse | null;
  month: string;
  loading: boolean;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  onThisMonth: () => void;
}

export default function ExpenseSummary({
  summary,
  month,
  loading,
  onPrevMonth,
  onNextMonth,
  onThisMonth,
}: Props) {
  const maxTotal = summary?.categories?.[0]?.total ?? 1;

  return (
    <div className="exp-summary-root">
      {/* Month navigator */}
      <div className="exp-summary-nav">
        <button type="button" className="exp-nav-btn" onClick={onPrevMonth}>
          &#x2039;
        </button>
        <span className="exp-summary-month">{formatMonth(month)}</span>
        <button type="button" className="exp-nav-btn" onClick={onNextMonth}>
          &#x203A;
        </button>
        <button type="button" className="exp-today-btn" onClick={onThisMonth}>
          This Month
        </button>
        {loading && <span className="exp-loading-dot" />}
      </div>

      {/* Grand total */}
      {summary && (
        <div className="exp-summary-total">
          <span className="exp-summary-total__label">Total Spending</span>
          <span className="exp-summary-total__value">
            USD {summary.grand_total.toFixed(2)}
          </span>
          <span className="exp-summary-total__count">
            {summary.expense_count} expense{summary.expense_count !== 1 ? 's' : ''}
          </span>
        </div>
      )}

      {/* Category breakdown */}
      {summary && summary.categories.length > 0 && (
        <div className="exp-summary-breakdown">
          {summary.categories.map((cat) => {
            const pct = maxTotal > 0 ? (cat.total / maxTotal) * 100 : 0;
            const color = CATEGORY_COLORS[cat.category] || '#6b7280';
            return (
              <div key={cat.category} className="exp-summary-row">
                <div className="exp-summary-row__header">
                  <span
                    className="exp-cat-badge"
                    style={{ background: color }}
                  >
                    {cat.category}
                  </span>
                  <span className="exp-summary-row__count">
                    {cat.count} {cat.count === 1 ? 'entry' : 'entries'}
                  </span>
                  <span className="exp-summary-row__amount">
                    {cat.currency || 'USD'} {cat.total.toFixed(2)}
                  </span>
                </div>
                <div className="exp-summary-bar">
                  <div
                    className="exp-summary-bar__fill"
                    style={{ width: `${pct}%`, background: color }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {summary && summary.categories.length === 0 && (
        <div className="exp-summary-empty">
          No expenses recorded for {formatMonth(month)}.
        </div>
      )}
    </div>
  );
}
